import { useSuspenseQuery } from "@tanstack/react-query";
import {
	createFileRoute,
	useNavigate,
	useParams,
} from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchOrganizationEstablishmentsQueryOptions } from "@/api/establishments";
import {
	fetchScheduleByIdQueryOptions,
	useDeleteSchedule,
} from "@/api/schedules";
import { fetchOrganizationStaffQueryOptions } from "@/api/staff";
import { Button } from "@/components/ui/button";
import { useApiParameters } from "@/hooks/useApiParameters";
import { ScheduleDeletionConfirmationModal } from "../-components/ScheduleDeletionConfirmationModal";
import { ScheduleForm } from "../-components/ScheduleForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/schedules/$scheduleId/",
)({
	beforeLoad: ({ context }) => {
		if (!context.userId) {
			throw new Error("Not authenticated");
		}
	},
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, scheduleId } = params;

		// Preload all required data
		context.queryClient.ensureQueryData(
			fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchOrganizationStaffQueryOptions(token, orgId),
		);
		context.queryClient.ensureQueryData(
			fetchScheduleByIdQueryOptions(userId, token, orgId, scheduleId),
		);

		return { userId, token, orgId, scheduleId };
	},
	component: ScheduleDetailsPage,
});

function ScheduleDetailsPage() {
	const { t } = useTranslation();
	const { orgId, scheduleId } = useParams({
		from: "/_auth/app/_app/org/$orgId/schedules/$scheduleId/",
	});
	const { userId, token } = Route.useLoaderData();
	const navigate = useNavigate();

	// Delete state and mutation
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const deleteScheduleMutation = useDeleteSchedule();

	// Get all required data
	const { data: establishments } = useSuspenseQuery(
		fetchOrganizationEstablishmentsQueryOptions(userId, token, orgId),
	);
	const { data: staff } = useSuspenseQuery(
		fetchOrganizationStaffQueryOptions(token, orgId),
	);
	const { data: schedule } = useSuspenseQuery(
		fetchScheduleByIdQueryOptions(userId, token, orgId, scheduleId),
	);

	// Handle successful form submission
	const handleSuccess = () => {
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
		});
	};

	// Handle delete
	const handleDeleteClick = useCallback(() => {
		setShowDeleteModal(true);
	}, []);

	const handleDeleteConfirm = useCallback(async () => {
		await deleteScheduleMutation.mutateAsync({
			token,
			organizationId: orgId,
			scheduleId,
		});
		setShowDeleteModal(false);
		navigate({
			to: "/app/org/$orgId/schedules",
			params: { orgId },
		});
	}, [deleteScheduleMutation, token, orgId, scheduleId, navigate]);

	const handleDeleteCancel = useCallback(() => {
		setShowDeleteModal(false);
	}, []);

	return (
		<>
			<div className="h-full flex flex-col overflow-hidden">
				<ScheduleForm
					mode="edit"
					orgId={orgId}
					token={token}
					establishments={establishments}
					staff={staff}
					initialData={schedule}
					onSuccess={handleSuccess}
					deleteButton={
						<Button
							type="button"
							variant="outline"
							className="text-error border-error hover:bg-error/10 px-2 sm:px-4"
							onClick={handleDeleteClick}
							disabled={deleteScheduleMutation.isPending}
							data-testid="delete-schedule-button"
						>
							<Trash2 className="h-4 w-4 sm:mr-2" />
							<span className="hidden sm:inline">{t("common.delete")}</span>
						</Button>
					}
				/>
			</div>

			{/* Schedule Deletion Confirmation Modal */}
			<ScheduleDeletionConfirmationModal
				isOpen={showDeleteModal}
				onClose={handleDeleteCancel}
				onConfirm={handleDeleteConfirm}
				schedule={schedule}
				organizationId={orgId}
				token={token}
				isDeleting={deleteScheduleMutation.isPending}
			/>
		</>
	);
}
